import type { ComponentChildren } from 'preact';
import { Link } from '@tanstack/react-router';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { MarketingFooter } from '../marketing/MarketingFooter.tsx';
import { useActiveSection } from '../../hooks/useActiveSection.ts';
import { docsPages, docsPageIndex } from '../../pages/docs/docsNav.ts';
import { useT } from '../../i18n.ts';
import '../../styles/marketing.css';

interface DocsLayoutProps {
  // Route of the page being rendered — used to mark the active nav item,
  // pull the page's section list, and derive the prev/next pager.
  path: string;
  title: string;
  lede: string;
  children: ComponentChildren;
}

// Two-column docs shell: a sticky left sidebar (same width and format as the
// product app's navigation) that navigates both between pages and within the
// current page, plus a readable content column. Rendered inside the shared
// marketing chrome, so the top brand nav still sits above it.
export function DocsLayout({
  path,
  title,
  lede,
  children,
}: DocsLayoutProps) {
  const t = useT();
  const index = docsPageIndex(path);
  const current = index >= 0 ? docsPages[index] : undefined;
  const prev = index > 0 ? docsPages[index - 1] : undefined;
  const next =
    index >= 0 && index < docsPages.length - 1
      ? docsPages[index + 1]
      : undefined;

  const sectionIds = current ? current.sections.map((s) => s.id) : [];
  const activeSection = useActiveSection(sectionIds);

  return (
    <div className="docs-page">
      <div className="docs-shell">
        <aside className="docs-sidebar">
          <nav className="docs-nav" aria-label="Documentation" data-gp-name="docs-sidebar">
            <span className="docs-nav-eyebrow">Docs</span>
            <ul className="docs-nav-list">
              {docsPages.map((page) => {
                const isActive = page.path === path;
                return (
                  <li key={page.path}>
                    <Link
                      to={page.path}
                      // `/docs` is a prefix of every other docs route, so the
                      // default fuzzy match would light up Getting Started on
                      // every page. Match exactly and drive the class from the
                      // page identity instead.
                      activeOptions={{ exact: true }}
                      activeProps={{ className: '' }}
                      className={
                        isActive ? 'docs-nav-link active' : 'docs-nav-link'
                      }
                    >
                      {t(page.labelKey)}
                    </Link>
                    {isActive && page.sections.length > 0 && (
                      <ul className="docs-nav-sections">
                        {page.sections.map((section) => (
                          <li key={section.id}>
                            <a
                              href={`#${section.id}`}
                              className={
                                activeSection === section.id
                                  ? 'docs-nav-section active'
                                  : 'docs-nav-section'
                              }
                            >
                              {section.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        <div className="docs-content">
          <header className="docs-header">
            <h1 className="docs-title">{title}</h1>
            <p className="docs-lede">{lede}</p>
          </header>

          <article className="mkt-doc-prose">{children}</article>

          {(prev || next) && (
            <nav className="docs-pager" aria-label="Page navigation" data-gp-name="docs-pager">
              {prev ? (
                <Link to={prev.path} className="docs-pager-link prev">
                  <span className="docs-pager-dir">
                    <ArrowLeftIcon className="docs-pager-arrow" aria-hidden="true" />
                    Previous
                  </span>
                  <span className="docs-pager-label">{t(prev.labelKey)}</span>
                </Link>
              ) : (
                <span />
              )}
              {next ? (
                <Link to={next.path} className="docs-pager-link next">
                  <span className="docs-pager-dir">
                    Next
                    <ArrowRightIcon className="docs-pager-arrow" aria-hidden="true" />
                  </span>
                  <span className="docs-pager-label">{t(next.labelKey)}</span>
                </Link>
              ) : (
                <span />
              )}
            </nav>
          )}
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}
